import React, { useState, useEffect } from 'react';
import { sliderService, mediaService } from '../../services/api';

function SlidersManagement() {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', subtitle: '', image: '', video_url: '', link: '', link_text: 'Learn More', position: 'home', sort_order: 0, is_active: true });
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => { loadSliders(); }, []);

  const loadSliders = async () => {
    setLoading(true);
    try { setSliders(await sliderService.index()); } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await sliderService.update(editingId, form);
      else await sliderService.store(form);
      setShowForm(false); setEditingId(null); setForm({ title: '', subtitle: '', image: '', video_url: '', link: '', link_text: 'Learn More', position: 'home', sort_order: 0, is_active: true });
      loadSliders();
    } catch (error) {
      const msg = error.response?.data?.message || Object.values(error.response?.data?.errors || {}).flat().join('\n') || 'Error saving slider';
      alert(msg);
    }
  };

  const handleEdit = (item) => { setForm({ ...item, image: item.image || '', video_url: item.video_url || '' }); setEditingId(item.id); setShowForm(true); };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this slider?')) return;
    try { await sliderService.destroy(id); loadSliders(); } catch (e) { alert('Error deleting'); }
  };

  const handleUpload = async (file, field) => {
    if (!file) return;
    setUploading(true);
    try {
      const media = await mediaService.upload(file, 'sliders');
      setForm(prev => ({ ...prev, [field]: media.url }));
    } catch (e) {
      alert('Upload failed');
    }
    setUploading(false);
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-100">Sliders / Banners</h2>
        <button onClick={() => { setShowForm(true); setEditingId(null); setForm({ title: '', subtitle: '', image: '', video_url: '', link: '', link_text: 'Learn More', position: 'home', sort_order: 0, is_active: true }); }} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition">+ Add Slider</button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-slate-900 rounded-2xl shadow-sm border border-slate-800 p-6 mb-6 space-y-5 max-w-3xl">
          <h3 className="font-bold text-lg text-slate-100">{editingId ? 'Edit Slider' : 'New Slider'}</h3>
          <div className="space-y-1">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Title</label>
            <input placeholder="Enter title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
          </div>
          <div className="space-y-1">
            <label className="text-[10px] uppercase text-slate-500 font-semibold">Subtitle</label>
            <textarea placeholder="Enter subtitle" value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" rows="2" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Slider Image</label>
              <input placeholder="Image URL" value={form.image || ''} onChange={e => setForm({ ...form, image: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
              <input type="file" accept="image/*" onChange={e => handleUpload(e.target.files[0], 'image')} className="w-full text-xs text-slate-400" />
              {form.image && <img src={form.image} alt="Preview" className="h-28 w-full object-cover rounded-lg border border-slate-800" />}
            </div>
            <div className="space-y-2">
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Slider Video (Optional)</label>
              <input placeholder="Video URL" value={form.video_url || ''} onChange={e => setForm({ ...form, video_url: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
              <input type="file" accept="video/mp4,video/webm,video/ogg,video/quicktime" onChange={e => handleUpload(e.target.files[0], 'video_url')} className="w-full text-xs text-slate-400" />
              {form.video_url && <video src={form.video_url} controls className="h-28 w-full object-cover rounded-lg border border-slate-800" />}
            </div>
          </div>
          {uploading && <div className="text-sm text-red-500 animate-pulse">Uploading media...</div>}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Link URL</label>
              <input placeholder="https://..." value={form.link} onChange={e => setForm({ ...form, link: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Link Text</label>
              <input placeholder="Button Text" value={form.link_text} onChange={e => setForm({ ...form, link_text: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Position</label>
              <input placeholder="home, about, etc." value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
            </div>
            <div className="space-y-1">
              <label className="text-[10px] uppercase text-slate-500 font-semibold">Sort Order</label>
              <input type="number" placeholder="0" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-red-500" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer"><input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="rounded bg-slate-950 border-slate-700 text-red-600 focus:ring-red-500" /> Active</label>
          <div className="flex gap-2 pt-2">
            <button type="submit" className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition">{editingId ? 'Update' : 'Save'}</button>
            <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-slate-800 text-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-700 transition">Cancel</button>
          </div>
        </form>
      )}

      <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden shadow-sm">
        <table className="min-w-full divide-y divide-slate-800">
          <thead className="bg-slate-950">
            <tr>
              <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Position</th>
              <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Order</th>
              <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {sliders.map((s) => (
              <tr key={s.id} className="hover:bg-slate-800/40 transition">
                <td className="px-6 py-4 text-sm text-slate-300 font-medium">{s.title || '-'}</td>
                <td className="px-6 py-4 text-sm text-slate-400">{s.position}</td>
                <td className="px-6 py-4 text-sm text-slate-400">{s.sort_order}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${s.is_active ? 'bg-red-600/10 text-red-500 border border-red-600/20' : 'bg-red-600/10 text-red-500 border border-red-600/20'}`}>
                    {s.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm space-x-3">
                  <button onClick={() => handleEdit(s)} className="text-red-500 hover:text-red-400 transition font-medium">Edit</button>
                  <button onClick={() => handleDelete(s.id)} className="text-red-500 hover:text-red-400 transition font-medium">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sliders.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-500 text-sm">No sliders found. Add one to display on the site.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SlidersManagement;
